import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAuctionAPI, adminUserAPI } from '../lib/api';
import { Shield, Gavel, Clock, Users, AlertTriangle, CheckCircle2, XCircle, Loader2, Play, Calendar, DollarSign, Ban, UserX, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function AdminAuctionControlPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [auctions, setAuctions] = useState([]);
  const [winners, setWinners] = useState([]);
  const [secondChanceOffers, setSecondChanceOffers] = useState([]);
  const [frozenUsers, setFrozenUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [extendDays, setExtendDays] = useState({});
  const [freezeReason, setFreezeReason] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/login'); return; }
    loadData();
  }, [user, isAdmin, navigate, authLoading]);

  const loadData = async () => {
    try {
      const [auctionsRes, winnersRes, offersRes, frozenRes, logsRes] = await Promise.all([
        adminAuctionAPI.list(),
        adminAuctionAPI.winners(),
        adminAuctionAPI.secondChanceOffers(),
        adminUserAPI.frozenUsers(),
        adminAuctionAPI.logs({ limit: 20 }),
      ]);
      setAuctions(auctionsRes.data.auctions || []);
      setWinners(winnersRes.data.winners || []);
      setSecondChanceOffers(offersRes.data.offers || []);
      setFrozenUsers(frozenRes.data.users || []);
      setLogs(logsRes.data.logs || []);
    } catch (err) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const handleExtendAuction = async (domainId) => {
    const days = parseInt(extendDays[domainId] || 7);
    try {
      await adminAuctionAPI.extend(domainId, { extend_days: days });
      toast.success(`Auction extended by ${days} days`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to extend auction');
    }
  };

  const handleEndAuction = async (domainId) => {
    if (!confirm('Are you sure you want to end this auction early?')) return;
    try {
      await adminAuctionAPI.endEarly(domainId, { reason: 'Admin action' });
      toast.success('Auction marked for ending');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end auction');
    }
  };

  const handleExtendPaymentDeadline = async (domainId) => {
    const days = parseInt(extendDays[`payment_${domainId}`] || 3);
    try {
      await adminAuctionAPI.extendPaymentDeadline(domainId, { extend_days: days });
      toast.success(`Payment deadline extended by ${days} days`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to extend deadline');
    }
  };

  const handleRunJobs = async () => {
    try {
      const res = await adminAuctionAPI.runJobs();
      toast.success(`Jobs executed: ${JSON.stringify(res.data.result)}`);
      loadData();
    } catch (err) {
      toast.error('Failed to run auction jobs');
    }
  };

  const handleFreezeUser = async (userId, isFrozen) => {
    try {
      await adminUserAPI.freezeUser(userId, { is_frozen: !isFrozen, reason: freezeReason });
      toast.success(`User ${isFrozen ? 'unfrozen' : 'frozen'}`);
      setFreezeReason('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  if (loading || authLoading) {
    return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;
  }

  const pendingPayments = winners.filter(w => w.status === 'pending_payment');
  const activeAuctions = auctions.filter(a => a.status === 'active');

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="admin-auction-control-page">
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-[#00E5FF]" />
          <h1 className="text-3xl font-bold text-white font-['Outfit'] tracking-tight">Auction Control Center</h1>
        </div>
        <Button onClick={handleRunJobs} className="bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="run-jobs-btn">
          <Play className="h-4 w-4 mr-2" /> Run Auction Jobs
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: Gavel, label: 'Active Auctions', value: activeAuctions.length, color: 'text-[#00E5FF]' },
          { icon: Clock, label: 'Pending Payments', value: pendingPayments.length, color: 'text-amber-400' },
          { icon: DollarSign, label: 'Second Chance', value: secondChanceOffers.length, color: 'text-purple-400' },
          { icon: UserX, label: 'Frozen Users', value: frozenUsers.length, color: 'text-red-400' },
        ].map((s, i) => (
          <div key={i} className="p-5 bg-[#0A0A0A] border border-white/5 rounded-sm">
            <s.icon className={`h-5 w-5 ${s.color} mb-3`} />
            <div className="text-2xl font-bold text-white font-['Outfit']">{s.value}</div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="auctions" className="space-y-6">
        <TabsList className="bg-[#0A0A0A] border border-white/5 p-1 rounded-sm">
          <TabsTrigger value="auctions" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black">Active Auctions</TabsTrigger>
          <TabsTrigger value="winners" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black">Winners & Payments</TabsTrigger>
          <TabsTrigger value="second-chance" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black">Second Chance</TabsTrigger>
          <TabsTrigger value="frozen" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black">Frozen Users</TabsTrigger>
          <TabsTrigger value="logs" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black">Activity Logs</TabsTrigger>
        </TabsList>

        {/* Active Auctions */}
        <TabsContent value="auctions">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Domain</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Current Bid</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Bids</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Ends</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Status</th>
                    <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {auctions.map(a => (
                    <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <Link to={`/domains/${a.id}`} className="text-sm text-white font-medium hover:text-[#00E5FF]">{a.full_name}</Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#00E5FF] font-bold">${(a.current_bid || a.min_bid || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{a.bid_count || 0}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{a.auction_end ? new Date(a.auction_end).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${a.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-zinc-800 text-zinc-500'}`}>{a.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Input
                            type="number"
                            value={extendDays[a.id] || ''}
                            onChange={(e) => setExtendDays(p => ({ ...p, [a.id]: e.target.value }))}
                            placeholder="7"
                            className="w-16 h-8 bg-[#050505] border-zinc-800 text-white rounded-sm text-xs"
                          />
                          <Button onClick={() => handleExtendAuction(a.id)} size="sm" variant="outline" className="h-8 border-zinc-700 text-zinc-400 text-xs">
                            <Calendar className="h-3 w-3 mr-1" /> Extend
                          </Button>
                          <Button onClick={() => handleEndAuction(a.id)} size="sm" variant="outline" className="h-8 border-red-500/30 text-red-400 text-xs">
                            <XCircle className="h-3 w-3 mr-1" /> End
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {auctions.length === 0 && <div className="text-center py-8 text-zinc-500">No auctions found</div>}
            </div>
          </div>
        </TabsContent>

        {/* Winners & Payments */}
        <TabsContent value="winners">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Domain</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Winner</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Amount</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Deadline</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Status</th>
                    <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {winners.map(w => (
                    <tr key={w.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-sm text-white font-medium">{w.domain_name}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{w.winner_id?.slice(0, 8)}...</td>
                      <td className="px-4 py-3 text-sm text-[#00E5FF] font-bold">${w.winning_bid?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{w.payment_deadline ? new Date(w.payment_deadline).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${
                          w.status === 'pending_payment' ? 'bg-amber-500/10 text-amber-400' :
                          w.status === 'paid' ? 'bg-green-500/10 text-green-400' :
                          'bg-red-500/10 text-red-400'
                        }`}>{w.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        {w.status === 'pending_payment' && (
                          <div className="flex items-center justify-end gap-2">
                            <Input
                              type="number"
                              value={extendDays[`payment_${w.domain_id}`] || ''}
                              onChange={(e) => setExtendDays(p => ({ ...p, [`payment_${w.domain_id}`]: e.target.value }))}
                              placeholder="3"
                              className="w-16 h-8 bg-[#050505] border-zinc-800 text-white rounded-sm text-xs"
                            />
                            <Button onClick={() => handleExtendPaymentDeadline(w.domain_id)} size="sm" variant="outline" className="h-8 border-zinc-700 text-zinc-400 text-xs">
                              Extend
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {winners.length === 0 && <div className="text-center py-8 text-zinc-500">No auction winners yet</div>}
            </div>
          </div>
        </TabsContent>

        {/* Second Chance Offers */}
        <TabsContent value="second-chance">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Domain</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Offered To</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Amount</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Deadline</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {secondChanceOffers.map(o => (
                    <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-sm text-white font-medium">{o.domain_name}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{o.offered_to_id?.slice(0, 8)}...</td>
                      <td className="px-4 py-3 text-sm text-[#00E5FF] font-bold">${o.offered_amount?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{o.offer_deadline ? new Date(o.offer_deadline).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${
                          o.status === 'pending' ? 'bg-amber-500/10 text-amber-400' :
                          o.status === 'accepted' ? 'bg-green-500/10 text-green-400' :
                          'bg-red-500/10 text-red-400'
                        }`}>{o.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {secondChanceOffers.length === 0 && <div className="text-center py-8 text-zinc-500">No second chance offers</div>}
            </div>
          </div>
        </TabsContent>

        {/* Frozen Users */}
        <TabsContent value="frozen">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6">
            <div className="mb-6">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Freeze Reason (for new freezes)</Label>
              <Input
                value={freezeReason}
                onChange={(e) => setFreezeReason(e.target.value)}
                placeholder="Enter reason for freezing account"
                className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm"
              />
            </div>
            
            {frozenUsers.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">No frozen users</div>
            ) : (
              <div className="space-y-3">
                {frozenUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-4 bg-[#050505] border border-red-500/20 rounded-sm">
                    <div>
                      <div className="text-sm text-white font-medium">{u.name}</div>
                      <div className="text-xs text-zinc-500">{u.email}</div>
                      {u.freeze_reason && <div className="text-xs text-red-400 mt-1">Reason: {u.freeze_reason}</div>}
                    </div>
                    <Button onClick={() => handleFreezeUser(u.id, true)} variant="outline" className="border-green-500/30 text-green-400 hover:border-green-500 rounded-sm">
                      <CheckCircle2 className="h-4 w-4 mr-2" /> Unfreeze
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Activity Logs */}
        <TabsContent value="logs">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Action</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Details</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Admin</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-sm bg-[#00E5FF]/10 text-[#00E5FF]">{log.action}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-zinc-400">
                        {log.domain_name || log.target_user_id?.slice(0, 8) || '-'}
                        {log.reason && <span className="ml-2 text-xs text-zinc-600">({log.reason})</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{log.admin_id?.slice(0, 8)}...</td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{new Date(log.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {logs.length === 0 && <div className="text-center py-8 text-zinc-500">No activity logs</div>}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
